import { Fragment, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Briefcase, Loader2 } from 'lucide-react'
import { Header } from '@/components/layout/Header'
import { PortfolioUpdateCard } from '@/components/portfolio/PortfolioUpdateCard'
import { ActivePositionsSection } from '@/components/portfolio/ActivePositionsSection'
import { PortfolioUpdateComposer } from '@/components/sheets/PortfolioUpdateComposer'
import { BroadcastSheet } from '@/components/sheets/BroadcastSheet'
import { SendSMSSheet } from '@/components/sheets/SendSMSSheet'
import { DocumentManagerSheet } from '@/components/sheets/DocumentManagerSheet'
import { UserManagementSheet } from '@/components/sheets/UserManagementSheet'
import { TrackedInsightsSheet } from '@/components/sheets/TrackedInsightsSheet'
import { ProfileSheet } from '@/components/sheets/ProfileSheet'
import { EditProfileModal } from '@/components/modals/EditProfileModal'
import { ImageViewerModal } from '@/components/modals/ImageViewerModal'
import { AccessGateOverlay } from '@/components/overlays/AccessGateOverlay'
import { ConversationHistorySheet } from '@/components/sheets/ConversationHistorySheet'
import { AITradingChat } from '@/components/sheets/AITradingChat'
import { PositionManagerSheet } from '@/components/sheets/PositionManagerSheet'
import { DateDivider } from '@/components/feed/DateDivider'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { isSameDay } from '@/lib/utils/dateLabel'
import { authRepo, profileRepo } from '@/lib/repositories'
import {
  useCurrentUser,
  useProfile,
  usePortfolioUpdates,
  useCreatePortfolioUpdate,
  useDeletePortfolioUpdate,
  useActivePositions,
  useTrackedIds,
} from '@/hooks'
import { type OverlayState } from '@/lib/types/overlay'

export function PortfolioPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data: user, isLoading: userLoading } = useCurrentUser()
  const { data: currentProfile } = useProfile(user?.username)
  const {
    data: updatesData,
    isLoading: updatesLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePortfolioUpdates()

  const updates = useMemo(
    () => updatesData?.pages.flatMap((page) => page.data) ?? [],
    [updatesData]
  )
  const createMutation = useCreatePortfolioUpdate()
  const deleteMutation = useDeletePortfolioUpdate()
  const { data: activePositions = [] } = useActivePositions()
  const { data: trackedIds = [] } = useTrackedIds()

  const [overlay, setOverlay] = useState<OverlayState>({ type: 'none' })
  const [showEditProfile, setShowEditProfile] = useState(false)

  const sentinelRef = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  })

  const isAdmin = user?.isAdmin || false
  const isApproved = user?.isApproved ?? false
  const isLoading = userLoading || updatesLoading

  const handleLogout = async () => {
    try {
      await authRepo.logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const getDisplayName = async (username: string): Promise<string> => {
    try {
      const profile = await profileRepo.getProfile(username)
      return profile?.displayName || username
    } catch {
      return username
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm(t('confirmDeletePortfolioUpdate'))) return
    try {
      await deleteMutation.mutateAsync(id)
      toast.success(t('updateDeleted'))
    } catch {
      toast.error(t('failedToDeleteUpdate'))
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sovereign-mesh flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center animate-pulse">
            <span
              className="text-black text-3xl"
              style={{
                fontFamily: "'garamond-atf-text', 'EB Garamond', serif",
                fontStyle: 'italic',
                WebkitTextStroke: '1px black',
              }}
            >
              S
            </span>
          </div>
          <div className="text-neutral-400 text-sm">{t('loading')}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-sovereign-mesh">
      <div className="fixed inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAyKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50 pointer-events-none" />

      <Header
        isAdmin={isAdmin}
        isApproved={isApproved}
        hasPhone={!!currentProfile?.phoneNumber}
        onBroadcastClick={() => setOverlay({ type: 'broadcast' })}
        onDocumentsClick={() => setOverlay({ type: 'documents' })}
        onUserManagementClick={() => setOverlay({ type: 'user-management' })}
        onSandboxClick={() => navigate('/sandbox')}
        activePage="portfolio"
        onPortfolioClick={() => navigate('/feed')}
        onTrackedClick={() => setOverlay({ type: 'tracked' })}
        onConversationHistoryClick={() =>
          setOverlay({ type: 'conversation-history' })
        }
        trackedCount={trackedIds.length}
        onProfileClick={async () => {
          const displayName = await getDisplayName(user?.username || '')
          setOverlay({
            type: 'profile',
            userId: user?.id || '',
            username: user?.username || '',
            displayName,
          })
        }}
        onLogoutClick={handleLogout}
      />

      <div
        className={!isApproved ? 'blur-md pointer-events-none select-none' : ''}
      >
        <main className="relative max-w-2xl mx-auto px-4 py-6 space-y-6">
          {/* Page Title */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/20 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">
                {t('portfolioTitle')}
              </h1>
              <p className="text-xs text-neutral-500">
                {t('portfolioSubtitle')}
              </p>
            </div>
          </div>

          {/* Welcome Message */}
          <div className="text-center">
            <p className="text-neutral-400 text-sm">
              {t('welcomeBack')}{' '}
              <span className="text-white">
                {currentProfile?.displayName || user?.username}
              </span>
              !
              {isAdmin && (
                <span className="ml-2 text-emerald-400">✨ {t('admin')}</span>
              )}
            </p>
          </div>

          {/* Active Positions */}
          <ActivePositionsSection
            isAdmin={isAdmin}
            onAddPosition={() => setOverlay({ type: 'position-manager' })}
            onEditPosition={(id) =>
              setOverlay({ type: 'position-manager', positionId: id })
            }
          />

          {/* Updates list */}
          {updates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-2xl bg-neutral-900/50 border border-white/[0.06] flex items-center justify-center mb-4">
                <Briefcase className="w-7 h-7 text-neutral-600" />
              </div>
              <p className="text-neutral-400 text-sm">
                {t('noPortfolioUpdatesYet')}
              </p>
              <p className="text-neutral-600 text-xs mt-1">
                {t('updatesWillAppear')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {updates.map((update, i) => {
                const prevDate =
                  i > 0 ? new Date(updates[i - 1]!.createdAt) : null
                const currentDate = new Date(update.createdAt)
                const showDivider =
                  i === 0 || (prevDate && !isSameDay(prevDate, currentDate))

                return (
                  <Fragment key={update.id}>
                    {showDivider && <DateDivider date={update.createdAt} />}
                    <PortfolioUpdateCard
                      update={update}
                      isAdmin={isAdmin}
                      onImageClick={(url) =>
                        setOverlay({ type: 'image-viewer', imageUrl: url })
                      }
                      onDelete={handleDelete}
                    />
                  </Fragment>
                )
              })}

              {/* Infinite scroll sentinel */}
              <div ref={sentinelRef} className="h-1" />

              {/* Loading more indicator */}
              {isFetchingNextPage && (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-neutral-500" />
                </div>
              )}

              {/* End of updates */}
              {!hasNextPage && updates.length > 0 && (
                <div className="text-center py-6">
                  <p className="text-neutral-500 text-sm">
                    {t('endOfFeed')}
                  </p>
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {!isApproved && <AccessGateOverlay />}

      {/* Overlays */}
      <PortfolioUpdateComposer
        open={overlay.type === 'portfolio-composer'}
        onClose={() => setOverlay({ type: 'none' })}
        onSubmit={async (data) => {
          await createMutation.mutateAsync({
            ...data,
            userId: user?.id || '',
          })
        }}
      />

      <PositionManagerSheet
        open={overlay.type === 'position-manager'}
        onClose={() => setOverlay({ type: 'none' })}
        position={
          overlay.type === 'position-manager' && overlay.positionId
            ? activePositions.find((p) => p.id === overlay.positionId)
            : undefined
        }
      />

      <ImageViewerModal
        open={overlay.type === 'image-viewer'}
        imageUrl={overlay.type === 'image-viewer' ? overlay.imageUrl : ''}
        onClose={() => setOverlay({ type: 'none' })}
      />

      <BroadcastSheet
        open={overlay.type === 'broadcast'}
        onClose={() => setOverlay({ type: 'none' })}
        onPostInsight={() => navigate('/feed')}
        onSendSMS={() => setOverlay({ type: 'send-sms' })}
        onPostPortfolioUpdate={() => setOverlay({ type: 'portfolio-composer' })}
      />

      <SendSMSSheet
        open={overlay.type === 'send-sms'}
        onClose={() => setOverlay({ type: 'none' })}
        onBack={() => setOverlay({ type: 'broadcast' })}
      />

      <TrackedInsightsSheet
        open={overlay.type === 'tracked'}
        onClose={() => setOverlay({ type: 'none' })}
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onAskAI={(_insightId, _ticker, _sentiment, _content) => {
          navigate('/feed')
        }}
      />

      <DocumentManagerSheet
        open={overlay.type === 'documents'}
        onClose={() => setOverlay({ type: 'none' })}
      />

      <UserManagementSheet
        open={overlay.type === 'user-management'}
        onClose={() => setOverlay({ type: 'none' })}
      />

      <ConversationHistorySheet
        open={overlay.type === 'conversation-history'}
        onClose={() => setOverlay({ type: 'none' })}
        isAdmin={isAdmin}
        onSelectConversation={(conv) => {
          if (conv.conversationType === 'sandbox') {
            navigate('/sandbox', {
              state: { conversationId: conv.id },
            })
          } else {
            setOverlay({
              type: 'resume-ai-chat',
              conversationId: conv.id,
              insightId: conv.insightId,
              ticker: conv.ticker,
            })
          }
        }}
      />

      <AITradingChat
        open={overlay.type === 'resume-ai-chat'}
        onClose={() => setOverlay({ type: 'none' })}
        insightId={
          overlay.type === 'resume-ai-chat' ? overlay.insightId : undefined
        }
        ticker={overlay.type === 'resume-ai-chat' ? overlay.ticker : undefined}
        resumeConversationId={
          overlay.type === 'resume-ai-chat' ? overlay.conversationId : undefined
        }
      />

      <ProfileSheet
        open={overlay.type === 'profile'}
        onClose={() => setOverlay({ type: 'none' })}
        userId={overlay.type === 'profile' ? overlay.userId : ''}
        username={overlay.type === 'profile' ? overlay.username : ''}
        displayName={
          overlay.type === 'profile' ? overlay.displayName : undefined
        }
        bio={
          overlay.type === 'profile' && overlay.username === user?.username
            ? currentProfile?.bio
            : undefined
        }
        hasPhone={!!currentProfile?.phoneNumber}
        insights={[]}
        isCurrentUser={
          overlay.type === 'profile' && user?.id === overlay.userId
        }
        onEditProfile={() => setShowEditProfile(true)}
      />

      <EditProfileModal
        open={showEditProfile}
        onClose={() => setShowEditProfile(false)}
        currentUsername={user?.username || ''}
        currentDisplayName={currentProfile?.displayName || ''}
        currentBio={currentProfile?.bio || ''}
        currentPhoneNumber={currentProfile?.phoneNumber || ''}
        currentBirthdate={currentProfile?.birthdate || ''}
        currentShippingAddress={currentProfile?.shippingAddress}
        currentEmail={user?.email || ''}
        usernameUpdatedAt={currentProfile?.usernameUpdatedAt}
        onSave={() => {}}
      />
    </div>
  )
}
